import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { bookingPortalAPI } from '../services/api';

function fmt12(t) {
  if (!t) return '';
  const [h, m] = t.slice(0, 5).split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12  = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

const STATUS_COLORS = {
  booked:      'bg-blue-100 text-blue-700',
  attended:    'bg-emerald-100 text-emerald-700',
  no_show:     'bg-red-100 text-red-700',
  cancelled:   'bg-gray-100 text-gray-500',
  rescheduled: 'bg-amber-100 text-amber-700',
};

// Find the first available slot for nextChildClass that starts at or after prevSlot ends (same date)
function findBackToBack(prevSlotId, nextChildClass, slots) {
  const prev = slots.find(s => s.id === prevSlotId);
  if (!prev) return null;
  const date     = prev.slot_date?.slice(0, 10);
  const afterTime = prev.slot_end_time || prev.slot_time;
  const candidates = slots
    .filter(s =>
      s.class_name === nextChildClass &&
      s.remaining > 0 &&
      s.slot_date?.slice(0, 10) === date &&
      s.slot_time >= afterTime
    )
    .sort((a, b) => a.slot_time.localeCompare(b.slot_time));
  return candidates[0] || null;
}

// ── Slot picker for one child ─────────────────────────────────
function SlotPicker({ child, slots, onBook, onCancel, booking,
                      selectedSlot, onSelect, suggestion, noBackToBack }) {
  const [notes,  setNotes]  = useState('');
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const mySlots = slots.filter(s => s.class_name === child.class_name && s.remaining > 0);

  const byDate = mySlots.reduce((acc, s) => {
    const d = s.slot_date?.slice(0, 10) ?? s.slot_date;
    if (!acc[d]) acc[d] = [];
    acc[d].push(s);
    return acc;
  }, {});

  const handle = async () => {
    if (!selectedSlot) return;
    setSaving(true); setError('');
    try {
      await onBook(child.student_id, selectedSlot, notes);
    } catch (e) {
      setError(e.response?.data?.error || 'Booking failed. Please try again.');
    }
    setSaving(false);
  };

  // Already booked — show confirmation card
  if (booking) {
    return (
      <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-100">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-bold text-emerald-800">✅ Slot Booked</p>
            <p className="text-sm text-emerald-700 mt-0.5">
              {booking.slot_date?.slice(0, 10)} · {fmt12(booking.slot_time)}
              {booking.slot_end_time ? ` – ${fmt12(booking.slot_end_time)}` : ''}
            </p>
            {booking.venue && <p className="text-xs text-emerald-600 mt-0.5">📍 {booking.venue}</p>}
            <p className="text-xs text-gray-400 font-mono mt-1">Ref: {booking.booking_ref || '—'}</p>
          </div>
          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATUS_COLORS[booking.booking_status] || STATUS_COLORS.booked}`}>
            {(booking.booking_status || 'booked').replace('_', ' ')}
          </span>
        </div>
        {['booked', 'rescheduled'].includes(booking.booking_status || 'booked') && (
          <button onClick={() => onCancel(booking.booking_id)}
            className="mt-3 text-xs text-red-500 hover:text-red-700 underline">
            Cancel this booking
          </button>
        )}
      </div>
    );
  }

  if (mySlots.length === 0) {
    return (
      <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100 text-center">
        <p className="text-sm text-amber-700 font-semibold">No available slots for Grade {child.class_name}</p>
        <p className="text-xs text-amber-600 mt-1">Please contact the school if you need assistance.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Back-to-back auto-selected banner */}
      {suggestion && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl px-3 py-2.5 flex items-start gap-2">
          <span className="text-base leading-none mt-0.5">⚡</span>
          <div className="text-xs text-blue-700">
            <p className="font-bold">Consecutive slot auto-selected</p>
            <p className="opacity-80 mt-0.5">
              {suggestion.slot_date?.slice(0, 10)} · {fmt12(suggestion.slot_time)}
              {suggestion.slot_end_time ? ` – ${fmt12(suggestion.slot_end_time)}` : ''} — keeps your family schedule back-to-back.
              You can still pick a different time below.
            </p>
          </div>
        </div>
      )}
      {/* No back-to-back available banner */}
      {noBackToBack && !suggestion && (
        <div className="bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5 flex items-start gap-2">
          <span className="text-base leading-none mt-0.5">⚠️</span>
          <div className="text-xs text-amber-700">
            <p className="font-bold">No consecutive slot available</p>
            <p className="opacity-80 mt-0.5">
              A back-to-back slot isn't free on the same day.
              Please pick any available time below — you can still book both children on the same day or different days.
            </p>
          </div>
        </div>
      )}

      {/* Slot grid grouped by date */}
      {Object.entries(byDate).map(([date, dateSlots]) => (
        <div key={date}>
          <p className="text-xs font-semibold text-gray-500 mb-2">
            {new Date(date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
          <div className="flex flex-wrap gap-2">
            {dateSlots.map(s => {
              const isSuggested = suggestion?.id === s.id;
              const isSelected  = selectedSlot === s.id;
              return (
                <button key={s.id} onClick={() => onSelect(s.id)}
                  className={`relative px-3 py-2 rounded-xl border text-sm font-semibold transition-all ${
                    isSelected
                      ? 'bg-primary-600 text-white border-primary-600 shadow-md scale-105'
                      : isSuggested
                        ? 'bg-blue-50 text-blue-700 border-blue-300 ring-2 ring-blue-200 scale-105'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-primary-400'
                  }`}>
                  {fmt12(s.slot_time)}
                  {isSuggested && !isSelected && (
                    <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-[9px] font-bold px-1 rounded-full leading-4">
                      AUTO
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Confirm section */}
      {selectedSlot && (
        <div className="pt-2">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Special Notes (optional)
          </label>
          <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Any specific topic you'd like to discuss…"
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400"/>
          {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
          <button onClick={handle} disabled={saving}
            className="mt-2 w-full bg-primary-600 text-white font-bold py-3 rounded-2xl text-sm disabled:opacity-60 hover:bg-primary-700 transition-colors">
            {saving ? 'Booking…' : 'Confirm Booking →'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────
export default function BookingPortal() {
  const { token }    = useParams();
  const [state,      setState]    = useState('loading');
  const [info,       setInfo]     = useState(null);
  const [error,      setError]    = useState('');
  const [cancelling, setCancel]   = useState(null);

  // Lifted selection state — keyed by student_id
  const [selections,  setSelections]  = useState({});
  const [suggestions, setSuggestions] = useState({});
  const [noBackToBack, setNoBTB]      = useState({});

  const load = useCallback(async () => {
    try {
      const r = await bookingPortalAPI.getPortal(token);
      if (r.data.is_closed) { setState('closed'); return; }
      setInfo(r.data);
      setState('form');
      setSelections({});
      setSuggestions({});
      setNoBTB({});
    } catch (e) {
      setError(e.response?.data?.error || 'This booking link is invalid or has expired.');
      setState('error');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // When a child at `idx` selects a slot, auto-suggest the next sibling's consecutive slot
  const handleSelect = useCallback((childIdx, slotId) => {
    if (!info) return;
    const { children, slots } = info;

    setSelections(prev => {
      const next = { ...prev, [children[childIdx].student_id]: slotId };

      if (childIdx < children.length - 1) {
        const nextChild  = children[childIdx + 1];
        const suggestion = findBackToBack(slotId, nextChild.class_name, slots);
        setSuggestions(s => ({ ...s, [nextChild.student_id]: suggestion }));
        setNoBTB(n => ({ ...n, [nextChild.student_id]: !suggestion }));
        // Auto-select the consecutive slot if found
        if (suggestion) next[nextChild.student_id] = suggestion.id;
      }

      return next;
    });
  }, [info]);

  const handleBook = async (student_id, slot_id, special_notes) => {
    await bookingPortalAPI.book(token, { student_id, slot_id, special_notes });
    load();
  };

  const handleCancel = async (booking_id) => {
    setCancel(booking_id);
    try {
      await bookingPortalAPI.cancel(token, { booking_id });
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Cancel failed');
    }
    setCancel(null);
  };

  // ── Static screens ────────────────────────────────────────────
  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"/>
          <p className="text-gray-500 text-sm">Loading your booking portal…</p>
        </div>
      </div>
    );
  }
  if (state === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50 p-6">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-sm w-full text-center">
          <div className="text-5xl mb-4">😕</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Link Not Found</h2>
          <p className="text-gray-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }
  if (state === 'closed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-sm w-full text-center">
          <div className="text-5xl mb-4">🔒</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Booking Closed</h2>
          <p className="text-gray-500 text-sm">The PTC booking window is not currently open.</p>
        </div>
      </div>
    );
  }

  const { campaign, primary_student, children, slots, school } = info;

  const allBooked   = children.every(c => !!c.booking_id && c.booking_status !== 'cancelled');
  const bookedCount = children.filter(c => !!c.booking_id && c.booking_status !== 'cancelled').length;

  const recommended = (() => {
    const booked = children.filter(c => c.booking_id && c.slot_time);
    if (booked.length < 2) return null;
    return [...booked].sort((a, b) => a.slot_time?.localeCompare(b.slot_time));
  })();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-xl">🏫</div>
          <div>
            <h1 className="font-black text-gray-900 text-base">{school?.name || 'The Kulish School'}</h1>
            <p className="text-xs text-gray-500">{campaign.name}</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">

        {/* Campaign info card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h2 className="font-bold text-gray-900">{campaign.name}</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {campaign.ptc_type === 'online' ? '💻 Online' : campaign.ptc_type === 'hybrid' ? '🔀 Hybrid' : '🏫 Physical'} PTC
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400">PTC Dates</p>
              <p className="text-sm font-semibold text-gray-700">{campaign.start_date} – {campaign.end_date}</p>
            </div>
          </div>
          <div className="bg-blue-50 rounded-xl px-3 py-2 text-xs text-blue-700">
            <strong>Parent:</strong> {primary_student.parent_name}
            {children.length > 1 && <> · <strong>{children.length} children</strong> registered</>}
          </div>
          {/* Smart scheduling hint — shown only before any booking */}
          {children.length > 1 && bookedCount === 0 && (
            <div className="mt-3 bg-emerald-50 rounded-xl px-3 py-2 text-xs text-emerald-700 flex items-start gap-2">
              <span>💡</span>
              <p>
                <strong>Smart scheduling is on.</strong> Select a slot for <strong>{children[0].student_name}</strong> first
                and we'll automatically pick a consecutive slot for your next child.
              </p>
            </div>
          )}
        </div>

        {/* Family progress (multi-child only) */}
        {children.length > 1 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <h3 className="text-sm font-bold text-gray-700 mb-3">Family Booking Progress</h3>
            <div className="flex items-center gap-2 mb-2">
              <div className="flex-1 bg-gray-100 rounded-full h-2">
                <div className="h-2 bg-emerald-500 rounded-full transition-all"
                  style={{ width: `${children.length ? (bookedCount / children.length) * 100 : 0}%` }}/>
              </div>
              <span className="text-xs font-bold text-gray-600">{bookedCount}/{children.length}</span>
            </div>
            {recommended && (
              <div className="mt-3 bg-emerald-50 rounded-xl p-3 border border-emerald-100">
                <p className="text-xs font-bold text-emerald-700 mb-2">📅 Your Family Schedule</p>
                {recommended.map((c, i) => (
                  <div key={c.student_id} className="flex items-center gap-2 text-xs text-emerald-700 mb-1">
                    <span className="font-semibold w-24 truncate">{c.student_name}</span>
                    <span>{c.slot_date?.slice(0, 10)} · {fmt12(c.slot_time)}</span>
                    {i < recommended.length - 1 && <span className="text-gray-400">↓</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Per-child booking cards */}
        {children.map((child, idx) => {
          const myBooking  = child.booking_id ? child : null;
          const suggestion = suggestions[child.student_id] || null;
          const noBTB      = noBackToBack[child.student_id] || false;
          return (
            <div key={child.student_id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
                <div>
                  <p className="font-bold text-gray-900">
                    {idx === 0 && children.length > 1 && !myBooking
                      ? <span className="text-primary-600">① </span>
                      : idx === 1 && children.length > 1 && !myBooking
                        ? <span className="text-primary-600">② </span>
                        : null}
                    {child.student_name}
                  </p>
                  <p className="text-xs text-gray-500">
                    Grade {child.class_name}{child.section ? ` – ${child.section}` : ''}
                    {child.admission_no ? ` · Adm. ${child.admission_no}` : ''}
                  </p>
                </div>
                {myBooking && myBooking.booking_status !== 'cancelled' && (
                  <span className="text-xs bg-emerald-100 text-emerald-700 font-semibold px-2.5 py-1 rounded-full">
                    ✓ Booked
                  </span>
                )}
              </div>
              <div className="p-5">
                <SlotPicker
                  child={child}
                  slots={slots}
                  booking={myBooking && myBooking.booking_status !== 'cancelled' ? myBooking : null}
                  onBook={handleBook}
                  onCancel={handleCancel}
                  selectedSlot={selections[child.student_id] || null}
                  onSelect={(slotId) => handleSelect(idx, slotId)}
                  suggestion={suggestion}
                  noBackToBack={noBTB}
                />
              </div>
            </div>
          );
        })}

        {/* All booked */}
        {allBooked && (
          <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-6 text-center">
            <div className="text-4xl mb-3">🎉</div>
            <h3 className="font-black text-emerald-900 text-lg mb-1">All Bookings Confirmed!</h3>
            <p className="text-sm text-emerald-700">
              You will receive a confirmation message with meeting details.
              After the meeting, a feedback link will be sent to you.
            </p>
          </div>
        )}

        {/* Important notes */}
        <div className="bg-amber-50 rounded-2xl border border-amber-100 p-4">
          <p className="text-xs font-bold text-amber-700 mb-1">📌 Important Notes</p>
          <ul className="text-xs text-amber-700 space-y-1 list-disc pl-4">
            <li>Bookings cannot be cancelled within {campaign.cutoff_hours || 12} hours of the meeting.</li>
            <li>Please arrive 5 minutes before your scheduled time.</li>
            <li>Post-meeting feedback is mandatory and will be sent after your appointment.</li>
            {campaign.ptc_type === 'online' && <li>Meeting links will be shared before the appointment.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}
